/**
 * Admin: the people, the equipment, and the prompt sandbox.
 *
 * Users are the reason this page exists now: the dashboard admits admins,
 * trainers and supervisors only, and this is where an admin adds them and
 * sets their role. Anyone with dashboard access can see the list; only an
 * admin can change it, and the backend enforces that too.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ListOrdered, Plus, QrCode as QrIcon } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { api, MOCK_MODE } from '../api';
import type { EquipmentRow, GenesisPreview, UserRole, UserRow } from '../api/types';
import { QrDialog } from '../components/QrCode';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { loadAuth } from '../auth/auth';
import {
  EmptyState,
  ErrorState,
  formatDateTime,
  Lightbox,
  Page,
  PageHeader,
  Panel,
  PhotoThumb,
  SkeletonRows,
  StatusBadge,
  timeAgo,
  Toast,
  useToast,
} from '../components/patterns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { go, href, type AdminTab } from '../router';

const ROLES: Array<{ value: UserRole; label: string; hint: string }> = [
  { value: 'technician', label: 'Technician', hint: 'Runs procedures on the glasses and phone' },
  { value: 'trainer', label: 'Trainer', hint: 'Dashboard, coaching notes, equipment' },
  { value: 'supervisor', label: 'Supervisor', hint: 'Dashboard, overrides, certificates' },
  { value: 'admin', label: 'Admin', hint: 'Everything, including users' },
];

const TABS: Array<{ value: AdminTab; label: string }> = [
  { value: 'users', label: 'Users' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'prompts', label: 'Prompt sandbox' },
];

/** A labelled form control. The label owns the field, so the hit area does too. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex min-w-0 flex-col gap-1.5">
      <span className="text-meta font-semibold text-ink-dim">{label}</span>
      {children}
    </label>
  );
}

const SELECT_CLASS =
  'h-9 w-full rounded-control border border-input bg-background px-3 text-support text-ink outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/40 disabled:opacity-50';

export function Admin({ tab }: { tab: AdminTab }) {
  const role = MOCK_MODE ? 'admin' : (loadAuth()?.user.role ?? 'technician');
  const isAdmin = role === 'admin';
  return (
    <Page>
      <PageHeader
        eyebrow="Admin"
        title="Manage"
        lede={
          isAdmin
            ? 'People, equipment and verification prompts.'
            : 'You can view these; an admin can change them.'
        }
      />

      <div className="flex gap-1 border-b border-line" role="tablist">
        {TABS.map((t) => (
          <a
            key={t.value}
            role="tab"
            aria-selected={t.value === tab}
            href={href({ name: 'admin', tab: t.value })}
            onClick={(e) => {
              e.preventDefault();
              go({ name: 'admin', tab: t.value });
            }}
            className={cn(
              '-mb-px border-b-2 px-3 py-2.5 text-support font-semibold transition-colors',
              t.value === tab
                ? 'border-ink text-ink'
                : 'border-transparent text-ink-dim hover:text-ink',
            )}
          >
            {t.label}
          </a>
        ))}
      </div>

      {tab === 'users' ? <UsersPanel isAdmin={isAdmin} /> : null}
      {tab === 'equipment' ? <EquipmentPanel canEdit={isAdmin || role === 'trainer'} /> : null}
      {tab === 'prompts' ? <PromptSandbox /> : null}
    </Page>
  );
}

// ---------- users ----------

function UsersPanel({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient();
  const me = MOCK_MODE ? undefined : loadAuth()?.user.id;
  const [toast, showToast, clearToast] = useToast();
  const usersQ = useQuery({ queryKey: ['users'], queryFn: () => api.listUsers() });
  const [form, setForm] = useState<{ email: string; fullName: string; role: UserRole }>({
    email: '',
    fullName: '',
    role: 'trainer',
  });
  const create = useMutation({
    mutationFn: () =>
      api.createUser({ ...form, email: form.email.trim(), fullName: form.fullName.trim() }),
    onSuccess: (u) => {
      setForm({ email: '', fullName: '', role: 'trainer' });
      showToast(`${u.fullName} added as ${u.role}`);
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => showToast(err instanceof Error ? err.message : String(err)),
  });
  const update = useMutation({
    mutationFn: (input: { id: string; body: { role?: UserRole; isActive?: boolean } }) =>
      api.updateUser(input.id, input.body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users'] }),
    onError: (err) => showToast(err instanceof Error ? err.message : String(err)),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!form.email.trim() || !form.fullName.trim()) return;
    create.mutate();
  }

  return (
    <>
      {isAdmin ? (
        <Panel title="Add a person">
          <form onSubmit={submit} className="grid items-end gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Full name">
              <Input
                value={form.fullName}
                onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                placeholder="Jordan Lee"
                required
              />
            </Field>
            <Field label="Work email">
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="jordan@company.com"
                required
              />
            </Field>
            <Field label="Role">
              <select
                className={SELECT_CLASS}
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value as UserRole })}
              >
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </Field>
            <Button
              type="submit"
              disabled={create.isPending}
              className="w-full sm:w-auto sm:justify-self-start"
            >
              <Plus /> {create.isPending ? 'Adding…' : 'Add'}
            </Button>
          </form>
          <p className="mt-3 text-meta text-ink-dim">
            {ROLES.find((r) => r.value === form.role)?.hint}. They sign in with their email; no
            password.
          </p>
        </Panel>
      ) : null}

      <Panel title="People" flush>
        {usersQ.isLoading ? (
          <SkeletonRows rows={4} />
        ) : usersQ.error ? (
          <ErrorState error={usersQ.error} retry={() => void usersQ.refetch()} />
        ) : (usersQ.data?.users.length ?? 0) === 0 ? (
          <EmptyState title="No users yet" />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Last sign-in</TableHead>
                  <TableHead>Status</TableHead>
                  {isAdmin ? <TableHead className="text-right">Action</TableHead> : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {usersQ.data!.users.map((u) => (
                  <UserTr
                    key={u.id}
                    u={u}
                    isMe={u.id === me}
                    isAdmin={isAdmin}
                    busy={update.isPending}
                    onRole={(role) => update.mutate({ id: u.id, body: { role } })}
                    onActive={(isActive) => update.mutate({ id: u.id, body: { isActive } })}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Panel>
      <Toast message={toast} onDone={clearToast} />
    </>
  );
}

function UserTr({
  u,
  isMe,
  isAdmin,
  busy,
  onRole,
  onActive,
}: {
  u: UserRow;
  isMe: boolean;
  isAdmin: boolean;
  busy: boolean;
  onRole: (r: UserRole) => void;
  onActive: (a: boolean) => void;
}) {
  return (
    <TableRow className={u.isActive ? undefined : 'opacity-55'}>
      <TableCell>
        <div className="font-medium text-ink">
          {u.fullName}
          {isMe ? <span className="font-normal text-ink-faint"> (you)</span> : null}
        </div>
        <div className="text-meta text-ink-faint">Added {formatDateTime(u.createdAt)}</div>
      </TableCell>
      <TableCell className="text-ink-dim">{u.email}</TableCell>
      <TableCell>
        {isAdmin ? (
          <select
            className={cn(SELECT_CLASS, 'h-8 w-36')}
            value={u.role}
            disabled={busy || isMe}
            title={isMe ? 'You cannot change your own role' : undefined}
            onChange={(e) => onRole(e.target.value as UserRole)}
          >
            {ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        ) : (
          <StatusBadge tone={u.role === 'admin' ? 'field' : 'neutral'}>{u.role}</StatusBadge>
        )}
      </TableCell>
      <TableCell className="text-ink-dim">
        {u.lastLoginAt ? timeAgo(u.lastLoginAt) : 'never'}
      </TableCell>
      <TableCell>
        {u.isActive ? (
          <StatusBadge tone="verified">Active</StatusBadge>
        ) : (
          <StatusBadge>Deactivated</StatusBadge>
        )}
      </TableCell>
      {isAdmin ? (
        <TableCell className="text-right">
          {isMe ? null : (
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => onActive(!u.isActive)}>
              {u.isActive ? 'Deactivate' : 'Reactivate'}
            </Button>
          )}
        </TableCell>
      ) : null}
    </TableRow>
  );
}

// ---------- equipment ----------

function EquipmentPanel({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const [toast, showToast, clearToast] = useToast();
  const eq = useQuery({ queryKey: ['equipment'], queryFn: () => api.listEquipment() });
  const [form, setForm] = useState({ name: '', assetTag: '', qrCodeValue: '', location: '' });
  const create = useMutation({
    mutationFn: (body: Partial<EquipmentRow>) => api.createEquipment(body),
    onSuccess: (row) => {
      setForm({ name: '', assetTag: '', qrCodeValue: '', location: '' });
      showToast(`${row.name} added`);
      void qc.invalidateQueries({ queryKey: ['equipment'] });
    },
    onError: (err) => showToast(err instanceof Error ? err.message : String(err)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteEquipment(id),
    onSuccess: () => {
      showToast('Archived');
      void qc.invalidateQueries({ queryKey: ['equipment'] });
    },
    onError: (err) => showToast(err instanceof Error ? err.message : String(err)),
  });
  const [qr, setQr] = useState<{ value: string; title: string }>();
  const [stepsFor, setStepsFor] = useState<{ value: string; title: string }>();

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!form.name.trim() || !form.qrCodeValue.trim()) return;
    create.mutate({
      name: form.name.trim(),
      assetTag: form.assetTag.trim(),
      qrCodeValue: form.qrCodeValue.trim(),
      location: form.location.trim() || undefined,
    });
  }

  return (
    <>
      {canEdit ? (
        <GenesisImportPanel
          onImported={(value, title) => {
            void qc.invalidateQueries({ queryKey: ['equipment'] });
            setStepsFor({ value, title });
          }}
        />
      ) : null}
      {canEdit ? (
        <Panel title="Add equipment">
          <form onSubmit={submit} className="grid items-end gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <Field label="Name">
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Pump Skid P-204"
                required
              />
            </Field>
            <Field label="Asset tag">
              <Input
                value={form.assetTag}
                onChange={(e) => setForm({ ...form, assetTag: e.target.value })}
                placeholder="P-204"
              />
            </Field>
            <Field label="QR code value">
              <Input
                className="font-mono"
                value={form.qrCodeValue}
                onChange={(e) => setForm({ ...form, qrCodeValue: e.target.value })}
                placeholder="EON-P204"
                required
              />
            </Field>
            <Field label="Location">
              <Input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="Bay 1"
              />
            </Field>
            <Button
              type="submit"
              disabled={create.isPending}
              className="w-full sm:w-auto sm:justify-self-start"
            >
              <Plus /> {create.isPending ? 'Adding…' : 'Add'}
            </Button>
          </form>
        </Panel>
      ) : null}

      <Panel title="Equipment" flush>
        {eq.isLoading ? (
          <SkeletonRows rows={3} />
        ) : eq.error ? (
          <ErrorState error={eq.error} retry={() => void eq.refetch()} />
        ) : (eq.data?.length ?? 0) === 0 ? (
          <EmptyState title="No equipment yet" body="Add the machines technicians will scan." />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Asset tag</TableHead>
                  <TableHead>QR value</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead className="text-right">Procedure</TableHead>
                  {canEdit ? <TableHead className="text-right"></TableHead> : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {eq.data!.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium text-ink">{r.name}</TableCell>
                    <TableCell className="text-ink-dim">{r.assetTag}</TableCell>
                    <TableCell
                      className="max-w-[14rem] truncate font-mono text-meta text-ink-dim"
                      title={r.qrCodeValue}
                    >
                      {r.qrCodeValue}
                    </TableCell>
                    <TableCell className="text-ink-dim">{r.location ?? '—'}</TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1.5">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setStepsFor({ value: r.qrCodeValue, title: r.name })}
                        >
                          <ListOrdered /> Steps
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setQr({ value: r.qrCodeValue, title: r.name })}
                        >
                          <QrIcon /> QR
                        </Button>
                      </div>
                    </TableCell>
                    {canEdit ? (
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={remove.isPending}
                          onClick={() => {
                            if (
                              window.confirm(
                                `Archive ${r.name}? Its QR code stops working and it leaves this list. Past sessions keep their record. Importing the lesson again brings it back.`,
                              )
                            )
                              remove.mutate(r.id);
                          }}
                        >
                          Archive
                        </Button>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Panel>
      {stepsFor ? (
        <StepsDialog
          qrCodeValue={stepsFor.value}
          title={stepsFor.title}
          onShowQr={() => {
            setQr(stepsFor);
            setStepsFor(undefined);
          }}
          onClose={() => setStepsFor(undefined)}
        />
      ) : null}
      {qr ? (
        <QrDialog
          value={qr.value}
          title={qr.title}
          open
          onOpenChange={(open) => {
            if (!open) setQr(undefined);
          }}
        />
      ) : null}
      <Toast message={toast} onDone={clearToast} />
    </>
  );
}

// ---------- Genesis import ----------

/**
 * A lesson authored in EON Genesis becomes scannable here: look the project up,
 * see what it carries, import. The import creates (or updates) an equipment row
 * whose QR value is GENESIS-<project id>, and that is the code to print.
 */
function GenesisImportPanel({
  onImported,
}: {
  onImported: (qrCodeValue: string, title: string) => void;
}) {
  const [projectId, setProjectId] = useState('');
  const [refresh, setRefresh] = useState(false);
  const [preview, setPreview] = useState<GenesisPreview>();
  const check = useMutation({
    mutationFn: (id: string) => api.genesisPreview(id),
    onSuccess: (p) => setPreview(p),
  });
  const run = useMutation({
    mutationFn: (body: { projectId: string; refresh: boolean }) => api.genesisImport(body),
    onSuccess: (out) => {
      if (out.equipment) onImported(out.equipment.qrCodeValue, out.equipment.name);
    },
  });
  const id = projectId.trim();
  const previewIsCurrent = preview?.projectId === id;

  function submitCheck(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    run.reset();
    check.mutate(id);
  }

  return (
    <Panel
      title="Import a lesson from Genesis"
      description="Paste the Genesis project id. The import creates the equipment and its steps, and gives you the QR code to print. The newest equipment becomes the default on the glasses."
    >
      <div className="flex flex-col gap-4">
        <form onSubmit={submitCheck} className="grid items-end gap-3 sm:grid-cols-[1fr_auto]">
          <Field label="Genesis project id">
            <Input
              className="font-mono"
              value={projectId}
              onChange={(e) => {
                setProjectId(e.target.value);
              }}
              placeholder="c744d1cf-5c51-4e1f-90c5-99f98dc75dd0"
              required
            />
          </Field>
          <Button
            type="submit"
            variant="outline"
            disabled={check.isPending || !id}
            className="w-full sm:w-auto"
          >
            {check.isPending ? 'Looking up…' : 'Look up'}
          </Button>
        </form>

        {check.error ? <ErrorState error={check.error} /> : null}

        {preview && previewIsCurrent ? (
          <div className="grid gap-3 rounded-control border border-line bg-background p-3 sm:grid-cols-[1fr_auto] sm:items-center">
            <div className="flex min-w-0 flex-col gap-1">
              <span className="font-medium text-ink">{preview.procedureTitle}</span>
              <span className="text-meta text-ink-dim">
                {preview.totalSteps} steps · {preview.stepsWithRenderedImages} with reference images
                · version {preview.sourceVersion}
                {preview.safetyLevel ? ` · ${preview.safetyLevel}` : ''}
              </span>
              <span className="font-mono text-meta text-ink-dim">QR {preview.qrCodeValue}</span>
              {preview.stepsWithRenderedImages < preview.totalSteps ? (
                <span className="text-meta text-ink-faint">
                  Steps without a rendered image are verified from the prompt alone. Render them in
                  Genesis and import again with the update box ticked.
                </span>
              ) : null}
            </div>
            <div className="flex flex-col items-start gap-2 sm:items-end">
              <label className="flex items-center gap-2 text-meta text-ink-dim">
                <input
                  type="checkbox"
                  checked={refresh}
                  onChange={(e) => setRefresh(e.target.checked)}
                />
                Update steps if already imported
              </label>
              <Button
                disabled={run.isPending}
                onClick={() => run.mutate({ projectId: id, refresh })}
              >
                {run.isPending ? 'Importing…' : 'Import'}
              </Button>
            </div>
          </div>
        ) : null}

        {run.error ? <ErrorState error={run.error} /> : null}
        {run.data ? (
          <p className="text-support text-ink">
            {run.data.materialized?.procedureCreated
              ? `Imported ${run.data.summary.procedureTitle}: ${run.data.materialized.stepsInserted} steps.`
              : run.data.materialized?.stepsUpdated
                ? `Updated ${run.data.materialized.stepsUpdated} steps of ${run.data.summary.procedureTitle}.`
                : `${run.data.summary.procedureTitle} was already imported at this version; nothing changed.`}{' '}
            Its steps are open; the QR code is in the list below.
          </p>
        ) : null}
      </div>
    </Panel>
  );
}

// ---------- procedure steps ----------

/**
 * What a technician will be walked through for one piece of equipment: each step's
 * instruction, the prompt the verifier judges the photo against, and the reference
 * image the worker can call up on the glasses. Read-only; the source of truth for
 * an imported lesson is Genesis, and a re-import is how it changes.
 */
function StepsDialog({
  qrCodeValue,
  title,
  onShowQr,
  onClose,
}: {
  qrCodeValue: string;
  title: string;
  onShowQr: () => void;
  onClose: () => void;
}) {
  const [lightbox, setLightbox] = useState<string>();
  const resolved = useQuery({
    queryKey: ['equipment-resolve', qrCodeValue],
    queryFn: () => api.resolveEquipment(qrCodeValue),
  });
  const procedureId = resolved.data?.activeProcedure?.id;
  const stepsQ = useQuery({
    queryKey: ['procedure-steps', procedureId],
    queryFn: () => api.listProcedureSteps(procedureId!),
    enabled: Boolean(procedureId),
  });
  const procedure = resolved.data?.activeProcedure;
  const steps = stepsQ.data?.steps ?? [];
  const withImages = steps.filter((s) => s.referenceImageUrl).length;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="flex max-h-[85vh] flex-col gap-4 overflow-hidden sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {procedure
              ? `${procedure.name} · version ${procedure.version} · ${steps.length} steps` +
                (stepsQ.data ? `, ${withImages} with a reference image` : '')
              : resolved.isLoading
                ? 'Loading the procedure…'
                : 'No active procedure for this equipment.'}
          </DialogDescription>
        </DialogHeader>

        <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">
          {resolved.error ? (
            <ErrorState error={resolved.error} retry={() => void resolved.refetch()} />
          ) : stepsQ.error ? (
            <ErrorState error={stepsQ.error} retry={() => void stepsQ.refetch()} />
          ) : resolved.isLoading || stepsQ.isLoading ? (
            <SkeletonRows rows={4} />
          ) : !procedure ? (
            <EmptyState
              title="Nothing to walk through yet"
              body="Import a lesson from Genesis, or attach a procedure to this equipment."
            />
          ) : (
            <ol className="flex flex-col divide-y divide-line">
              {steps.map((s) => (
                <li key={s.id} className="grid gap-3 py-3 sm:grid-cols-[96px_1fr]">
                  {s.referenceImageUrl ? (
                    <PhotoThumb
                      src={s.referenceImageUrl}
                      alt={`Reference for step ${s.stepNumber}`}
                      className="aspect-square w-24"
                      onOpen={setLightbox}
                    />
                  ) : (
                    <div className="flex aspect-square w-24 items-center justify-center rounded-control border border-dashed border-line text-center text-meta text-ink-faint">
                      No image
                    </div>
                  )}
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="font-medium text-ink">
                      <span className="mr-2 font-mono text-meta text-ink-faint tabular-nums">
                        {String(s.stepNumber).padStart(2, '0')}
                      </span>
                      {s.title}
                    </span>
                    <p className="text-support text-ink-dim">{s.instruction}</p>
                    {s.verificationPrompt ? (
                      <details className="text-meta text-ink-dim">
                        <summary className="cursor-pointer select-none text-ink-faint">
                          Verification prompt
                        </summary>
                        <p className="mt-1 whitespace-pre-wrap">{s.verificationPrompt}</p>
                      </details>
                    ) : (
                      <span className="text-meta text-ink-faint">No verification on this step</span>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line pt-3">
          <code className="text-meta text-ink-dim">{qrCodeValue}</code>
          <Button variant="outline" onClick={onShowQr}>
            <QrIcon /> Show QR
          </Button>
        </div>
      </DialogContent>
      <Lightbox src={lightbox} onClose={() => setLightbox(undefined)} />
    </Dialog>
  );
}

// ---------- prompt sandbox ----------

function PromptSandbox() {
  const [stepId, setStepId] = useState('');
  const [prompt, setPrompt] = useState(
    'Look at this photo. Confirm a padlock is visible on the disconnect.',
  );
  const run = useMutation({ mutationFn: () => api.testPrompt(stepId.trim(), { prompt }) });
  return (
    <div className="grid gap-5 lg:grid-cols-2 lg:items-start">
      <Panel title="Try a verification prompt">
        <div className="flex flex-col gap-3">
          <p className="text-support text-ink-dim">
            Check how a step's prompt reads before it goes live. The verdict comes back the same
            shape the glasses get. It is a mock unless the verifier has a live key.
          </p>
          <Field label="Step id">
            <Input
              className="font-mono"
              value={stepId}
              onChange={(e) => setStepId(e.target.value)}
              placeholder="Paste the step's id"
            />
          </Field>
          <Field label="Prompt">
            <textarea
              className="min-h-28 w-full resize-y rounded-control border border-input bg-background px-3 py-2 text-support outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/40"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />
          </Field>
          <div>
            <Button
              onClick={() => run.mutate()}
              disabled={run.isPending || !stepId.trim() || !prompt.trim()}
            >
              {run.isPending ? 'Running…' : 'Run'}
            </Button>
          </div>
        </div>
      </Panel>

      <Panel title="Verdict">
        {run.error ? (
          <ErrorState error={run.error} />
        ) : run.data ? (
          <div className="flex flex-col gap-2.5">
            <div className="flex items-center gap-2">
              <StatusBadge tone={run.data.result.verified ? 'verified' : 'retry'}>
                {run.data.result.verified ? 'Verified' : 'Retake'}
              </StatusBadge>
              <span className="text-meta text-ink-faint">
                {run.data.result.confidence} confidence · {run.data.mode}
              </span>
            </div>
            <p className="text-support text-ink">{run.data.result.message}</p>
            <p className="text-meta text-ink-dim">{run.data.result.detail}</p>
          </div>
        ) : (
          <EmptyState
            title="No verdict yet"
            body="Run a prompt to see what the verifier would say."
          />
        )}
      </Panel>
    </div>
  );
}
